import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FileText,
  FileCheck,
  FilePlus,
  Trash2,
  Eye,
  Download,
  MoreVertical,
  Upload,
  Shield,
  HeartPulse,
  AlertTriangle,
  Star,
  FolderOpen,
  Calendar,
  User,
  Loader2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile } from '@/lib/gcsStorage';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';

// Document type configuration
const DOCUMENT_TYPES = {
  contract: {
    label: 'Contract',
    icon: FileCheck,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  sickness: {
    label: 'Sickness Record',
    icon: HeartPulse,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  disciplinary: {
    label: 'Disciplinary',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  performance: {
    label: 'Performance Review',
    icon: Star,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  other: {
    label: 'Other',
    icon: FileText,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  }
};

export function ProfileDocuments({
  documents,
  documentsLoading,
  userProfile,
  company,
  currentUserProfile,
  userRole,
  isOwnProfile,
  onDocumentsChange
}) {
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    document_type: 'other',
    effective_date: '',
    expiry_date: '',
    file: null
  });

  // Check if user can upload documents (Admin/Manager, or own sickness docs)
  const canUploadAll = userRole === 'Admin' || userRole === 'Manager';
  const canUploadSickness = isOwnProfile;
  const canUpload = canUploadAll || canUploadSickness;
  const canDelete = userRole === 'Admin' || userRole === 'Manager';

  // Filter documents by type
  const filteredDocuments = filterType === 'all'
    ? documents
    : documents.filter(doc => doc.document_type === filterType);

  // Group documents by type for display
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const type = doc.document_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File must be less than 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title.trim()) {
      toast.error('Please provide a title and select a file');
      return;
    }

    // Check permissions for document type
    if (!canUploadAll && uploadForm.document_type !== 'sickness') {
      toast.error('You can only upload sickness documents for your own profile');
      return;
    }

    setUploading(true);
    try {
      // Upload file to storage
      const fileExt = uploadForm.file.name.split('.').pop();
      const fileName = `${userProfile.id}/${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;

      const { publicUrl } = await gcsUploadFile(uploadForm.file, {
        folderPath: `project-files/user-documents`,
        fileName: fileName,
        contentType: uploadForm.file.type
      });

      // Create document record
      const { error: insertError } = await supabase
        .from('user_documents')
        .insert({
          user_id: userProfile.id,
          company_id: company.id,
          document_type: uploadForm.document_type,
          title: uploadForm.title.trim(),
          description: uploadForm.description.trim() || null,
          file_url: publicUrl,
          file_name: uploadForm.file.name,
          file_size: uploadForm.file.size,
          uploaded_by: currentUserProfile.id,
          effective_date: uploadForm.effective_date || null,
          expiry_date: uploadForm.expiry_date || null
        });

      if (insertError) throw insertError;

      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setUploadForm({
        title: '',
        description: '',
        document_type: 'other',
        effective_date: '',
        expiry_date: '',
        file: null
      });
      onDocumentsChange?.();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      const { error } = await supabase
        .from('user_documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;

      toast.success('Document deleted');
      setDeleteConfirm(null);
      onDocumentsChange?.();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (documentsLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg animate-pulse">
            <div className="h-10 w-10 bg-gray-200 rounded" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-200 rounded w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filter and upload button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Documents</SelectItem>
              {Object.entries(DOCUMENT_TYPES).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500">
            {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
          </span>
        </div>

        {canUpload && (
          <Button onClick={() => setShowUploadDialog(true)} className="gap-2">
            <FilePlus className="h-4 w-4" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Documents list */}
      {filteredDocuments.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([type, docs]) => {
            const config = DOCUMENT_TYPES[type] || DOCUMENT_TYPES.other;
            const Icon = config.icon;

            return (
              <div key={type}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`h-4 w-4 ${config.color}`} />
                  <h3 className="text-sm font-semibold text-gray-900">{config.label}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {docs.length}
                  </Badge>
                </div>

                <div className="space-y-2">
                  {docs.map((doc) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-center gap-4 p-4 rounded-lg border ${config.bgColor} ${config.borderColor}`}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${config.bgColor} border ${config.borderColor} flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${config.color}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{doc.title}</h4>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          {doc.file_name && (
                            <span className="truncate max-w-[150px]">{doc.file_name}</span>
                          )}
                          {doc.file_size && (
                            <span>{formatFileSize(doc.file_size)}</span>
                          )}
                          {doc.effective_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(doc.effective_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-1">{doc.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(doc.created_date), { addSuffix: true })}
                        </span>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={doc.file_url} download={doc.file_name}>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </a>
                            </DropdownMenuItem>
                            {canDelete && (
                              <DropdownMenuItem
                                onClick={() => setDeleteConfirm(doc)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-gray-500">
              <FolderOpen className="h-12 w-12 mb-3 opacity-50" />
              <p className="font-medium">No documents found</p>
              <p className="text-sm mt-1">
                {canUpload
                  ? 'Upload documents to keep employee records organized'
                  : 'Documents will appear here when added'}
              </p>
              {canUpload && (
                <Button
                  variant="outline"
                  className="mt-4 gap-2"
                  onClick={() => setShowUploadDialog(true)}
                >
                  <FilePlus className="h-4 w-4" />
                  Upload First Document
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Add a document to {isOwnProfile ? 'your' : `${userProfile?.first_name}'s`} profile
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="doc-type">Document Type</Label>
              <Select
                value={uploadForm.document_type}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, document_type: value }))}
                disabled={!canUploadAll}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOCUMENT_TYPES).map(([key, config]) => (
                    <SelectItem
                      key={key}
                      value={key}
                      disabled={!canUploadAll && key !== 'sickness'}
                    >
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!canUploadAll && (
                <p className="text-xs text-gray-500">You can only upload sickness documents</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-title">Title *</Label>
              <Input
                id="doc-title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Employment Contract 2024"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-description">Description</Label>
              <Textarea
                id="doc-description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional notes about this document"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="doc-effective">Effective Date</Label>
                <Input
                  id="doc-effective"
                  type="date"
                  value={uploadForm.effective_date}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, effective_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc-expiry">Expiry Date</Label>
                <Input
                  id="doc-expiry"
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-file">File *</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-gray-300 transition-colors">
                <input
                  id="doc-file"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="doc-file" className="cursor-pointer">
                  {uploadForm.file ? (
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">{uploadForm.file.name}</span>
                      <span className="text-gray-500">({formatFileSize(uploadForm.file.size)})</span>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">Click to select a file</p>
                      <p className="text-xs mt-1">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ProfileDocuments;
